import type { ApiBody, ApiData } from '@app/api/typed';

/**
 * The chat entry point of an agent (aim-5wc, prototype v2 of 08/10 §2–§3): how the Chat delivers the question to
 * one identity. The console keeps it per identity; without a configuration the Segura default applies.
 *
 * Everything here is pure: the drawer and the page compose it, the tests assert it without rendering.
 */

export type ChatEntryState = ApiData<'/api/v1/identities/{id}/chat-entry', 'get'>;
export type ChatEntry = ChatEntryState['entry'];
export type ChatEntryProtocol = ChatEntry['protocol'];
export type ChatEntryUpdate = ApiBody<'/api/v1/identities/{id}/chat-entry', 'put'>;
export type ChatEntryTestRequest = ApiBody<'/api/v1/identities/{id}/chat-entry/test', 'post'>;
export type ChatEntryTestResult = ApiData<'/api/v1/identities/{id}/chat-entry/test', 'post'>;
export type ChatDefaultAgent = ApiData<'/api/v1/agents/chat-default', 'get'>;

/** The protocols, in the order the combobox lists them (§3.2). */
export const PROTOCOLS: readonly ChatEntryProtocol[] = ['a2a_1_0', 'a2a_0_3', 'ag_ui'];

/** The header of the person token by default — a contract value, the same in both languages. */
export const AUTHORIZATION = 'Authorization';

/** At most 5 extra headers (§2.4). */
export const MAX_HEADERS = 5;

/**
 * The protocol as the summary and the trail show it. Contract values (`A2A 1.0`, `AG-UI`) are the same in both
 * languages, so they compose in code and do not go to the catalog (§3.6).
 */
export function protocolLabel(protocol: ChatEntryProtocol): string {
  switch (protocol) {
    case 'a2a_1_0':
      return 'A2A 1.0';
    case 'a2a_0_3':
      return 'A2A 0.3';
    case 'ag_ui':
      return 'AG-UI';
  }
}

/** `customer-service.agents:8080/` — the short address of the summary line (§3.1). */
export function shortAddress(entry: Pick<ChatEntry, 'service' | 'port' | 'path'>): string {
  return `${entry.service}:${entry.port}${entry.path}`;
}

/** The mask of a secret: `••••` and the hint (§2.4). Data with punctuation: composed in code. */
export function secretMask(hint: string | null | undefined): string {
  return `••••${hint ?? ''}`;
}

// --- the form ---------------------------------------------------------------------

/** One extra header as the form edits it. */
export interface HeaderForm {
  name: string;
  sensitive: boolean;
  /** What is typed. For a stored secret in «keep» mode it stays empty: empty keeps the stored value. */
  value: string;
  /** The hint of the stored secret with this name; `null` when there is none. */
  storedHint: string | null;
  /** `true` while the admin replaces a stored secret (the «Trocar» field is open). */
  replacing: boolean;
}

export type TokenHeaderMode = 'authorization' | 'other';

export interface ChatEntryForm {
  service: string;
  /** Text, as the field holds it; the schema checks the range. */
  port: string;
  path: string;
  protocol: ChatEntryProtocol;
  tokenHeaderMode: TokenHeaderMode;
  tokenHeaderName: string;
  headers: HeaderForm[];
  opensChat: boolean;
}

/** The form opened on a state: the configured entry, or the Segura default. A secret never comes back as value. */
export function formFromState(state: ChatEntryState): ChatEntryForm {
  const { entry } = state;
  const authorization = entry.person_token_header.toLowerCase() === AUTHORIZATION.toLowerCase();
  return {
    service: entry.service,
    port: String(entry.port),
    path: entry.path,
    protocol: entry.protocol,
    tokenHeaderMode: authorization ? 'authorization' : 'other',
    tokenHeaderName: authorization ? '' : entry.person_token_header,
    headers: entry.extra_headers.map((header) => ({
      name: header.name,
      sensitive: header.sensitive,
      // The mask NEVER becomes a value (the trap `SiteModel.ts` records): a stored secret opens empty.
      value: header.sensitive ? '' : (header.value ?? ''),
      storedHint: header.sensitive ? (header.hint ?? '') : null,
      replacing: false,
    })),
    opensChat: state.opens_chat,
  };
}

/** A new, empty extra header. */
export function newHeader(): HeaderForm {
  return { name: '', sensitive: false, value: '', storedHint: null, replacing: false };
}

/** The header of the person token the form says. */
export function tokenHeaderOf(
  form: Pick<ChatEntryForm, 'tokenHeaderMode' | 'tokenHeaderName'>,
): string {
  return form.tokenHeaderMode === 'authorization' ? AUTHORIZATION : form.tokenHeaderName.trim();
}

/** The entry the form describes, as the PUT and the test send it. */
export function entryOf(form: ChatEntryForm): NonNullable<ChatEntryUpdate['entry']> {
  return {
    service: form.service.trim(),
    port: Number(form.port),
    path: form.path.trim(),
    protocol: form.protocol,
    person_token_header: tokenHeaderOf(form),
    extra_headers: form.headers.map((header) => ({
      name: header.name.trim(),
      sensitive: header.sensitive,
      value: header.value,
    })),
  };
}

/** Whether the address part of the form is what the state has (the D8 checkbox does not count). */
export function addressUnchanged(form: ChatEntryForm, state: ChatEntryState): boolean {
  const initial = formFromState(state);
  const { opensChat: _a, ...now } = form;
  const { opensChat: _b, ...before } = initial;
  return JSON.stringify(now) === JSON.stringify(before);
}

/**
 * The body of the PUT, a full replacement. On an agent with the Segura default whose address the admin did not
 * touch, `entry` is `null`: only checking «Abrir o Chat neste agente» must not turn the entry «configured» (that
 * would switch off the old-agent detection of the executor).
 */
export function updateOf(form: ChatEntryForm, state: ChatEntryState): ChatEntryUpdate {
  const keepDefault = !state.custom && addressUnchanged(form, state);
  return { entry: keepDefault ? null : entryOf(form), opens_chat: form.opensChat };
}

/** The body of the test: what the drawer shows, saved or not. The extra headers do not go to the executor. */
export function testRequestOf(form: ChatEntryForm): ChatEntryTestRequest {
  return { entry: { ...entryOf(form), extra_headers: [] } };
}

/** The call address of the preview: built from the parts, like the executor builds it (§2.2). */
export function callAddress(form: Pick<ChatEntryForm, 'service' | 'port' | 'path'>): string {
  return `http://${form.service.trim()}.svc.cluster.local:${form.port.trim()}${form.path.trim()}`;
}

// --- the test ---------------------------------------------------------------------

export type TestNoticeKind =
  'target' | 'card' | 'versionMismatch' | 'noCard' | 'agUiNoCard' | 'unreachable';

export interface TestNotice {
  kind: TestNoticeKind;
  tone: 'success' | 'info' | 'warning';
  params: Record<string, string>;
}

/**
 * The lines of the test result, in the order of §2.7: the target and the connection, then the card. A target
 * refusal never gets here — it is a 422, shown by its code.
 */
export function testNotices(
  result: ChatEntryTestResult,
  protocol: ChatEntryProtocol,
  port: string,
): TestNotice[] {
  if (!result.connection.ok) {
    return [
      {
        kind: 'unreachable',
        tone: 'warning',
        params: { port, detail: result.connection.detail ?? '' },
      },
    ];
  }
  const notices: TestNotice[] = [
    { kind: 'target', tone: 'success', params: { agent: result.agent, port } },
  ];
  switch (result.card.status) {
    case 'not_applicable':
      notices.push({ kind: 'agUiNoCard', tone: 'info', params: {} });
      break;
    case 'absent':
      notices.push({ kind: 'noCard', tone: 'info', params: {} });
      break;
    case 'ok':
      if (result.version_match === false) {
        notices.push({
          kind: 'versionMismatch',
          tone: 'warning',
          params: {
            cardVersion: result.card.version ?? '',
            entryVersion: protocolLabel(protocol).replace('A2A ', ''),
          },
        });
      } else {
        notices.push({
          kind: 'card',
          tone: 'success',
          params: { name: result.card.name ?? '', version: result.card.version ?? '' },
        });
      }
      break;
    case 'skipped':
      break;
  }
  return notices;
}

// --- D8: the agent the Chat opens on ---------------------------------------------------

/** The browser key of the last agent the person used. A per-viewer convenience: it can be empty or throw. */
export const LAST_AGENT_KEY = 'aim.chat.lastAgent';

export function readLastAgent(): string | null {
  try {
    return window.localStorage.getItem(LAST_AGENT_KEY);
  } catch {
    return null;
  }
}

export function writeLastAgent(slug: string): void {
  try {
    window.localStorage.setItem(LAST_AGENT_KEY, slug);
  } catch {
    // A private window or blocked storage: the Chat still opens, on the mark or on the first of the list.
  }
}

/**
 * The agent the Chat opens on (D8): the marked one; without a mark, the last the person used in this browser; without
 * both, the first of the list in alphabetical order. Each candidate counts only if it is in the list — an agent that
 * lost its contract is not offered.
 */
export function initialAgent(
  slugs: readonly string[],
  marked: string | null,
  lastUsed: string | null,
): string | null {
  if (marked && slugs.includes(marked)) return marked;
  if (lastUsed && slugs.includes(lastUsed)) return lastUsed;
  const sorted = [...slugs].sort((a, b) => a.localeCompare(b));
  return sorted[0] ?? null;
}

// --- the conversation window: the trail and the link to Decisions (T16) ------------------------

/**
 * The margins of the window (aim-6ph.9, decision T4 (a) of the mrolim, 09/10/2026): the trail and the link «Ver as
 * decisões desta conversa» use the same window, the start of the question minus 5 s and the end of the answer plus
 * 5 s. The link used the end plus 60 s, and it caught the next question of the same person.
 */
export const WINDOW_BEFORE_MS = 5_000;
export const WINDOW_AFTER_MS = 5_000;

export interface ConversationWindow {
  /** The agent asked, by its slug. The link carries it only for the band of Decisions (T16), and not as a filter. */
  agent: string;
  sub: string;
  startedAt: Date;
  finishedAt: Date;
  /**
   * The id of the question (aim-6ph.10): the trail asks for the decisions of this id and the person. Absent in an answer
   * of an executor before the id, and then the trail reads by the person and the window.
   */
  conversationId?: string;
}

/** The href of Decisions (T16) in `navigation.ts`; a test keeps the two equal. */
export const DECISIONS_PATH = '/decisoes';

/** The `since` and the `until` of the conversation window, in RFC 3339. */
export function conversationRange(window: ConversationWindow): { since: string; until: string } {
  return {
    since: new Date(window.startedAt.getTime() - WINDOW_BEFORE_MS).toISOString(),
    until: new Date(window.finishedAt.getTime() + WINDOW_AFTER_MS).toISOString(),
  };
}

/**
 * `/decisoes?agent=…&sub=…&since=…&until=…`: Decisions by the person and the window, without the identity filter
 * (aim-6ph.9), so the list also shows the agent that the asked agent delegated to.
 */
export function decisionsLink(window: ConversationWindow): string {
  const params = new URLSearchParams({ agent: window.agent, sub: window.sub, ...conversationRange(window) });
  return `${DECISIONS_PATH}?${params.toString()}`;
}
